import { IconButton } from "@mui/material";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
    closestCenter, DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReactNode, useEffect, useState } from "react";
import { api } from "../../../api/API";
import { Task } from "../../../api/Tasks";
import { applyOrder } from "../../../misc/dragOrder";
import { useSnackAction } from "../../../misc/SnackAction";

// SortableDayTasks is the open tasks of one day, dragged into order by their grip. A
// drag starts only from the grip, so the list still scrolls and a row still swipes.
export function SortableDayTasks(props: {
    tasks: Task[];

    // renderRow draws one row, with the grip at its right-hand end
    renderRow: (task: Task, grip: ReactNode) => ReactNode;
}) {
    // the new order shows at once and stands in for the server's until the list changes
    const [order, setOrder] = useState<number[] | null>(null);
    const ids = props.tasks.map(task => task.id).join(",");
    useEffect(() => setOrder(null), [ids]);

    const tasks = applyOrder(props.tasks, order);

    // a touch holds for a moment before it drags, and a mouse moves a little, so a tap
    // on the grip doesn't start one
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    );

    const reorder = useSnackAction(async (input: { ids: number[] }) => {
        try {
            await api.tasks.reorder(input);
        } catch (e) {
            setOrder(null);
            throw e;
        }
    }, { success: "Saved" });

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;

        const current = tasks.map(task => task.id);
        const next = arrayMove(current, current.indexOf(Number(e.active.id)), current.indexOf(Number(e.over.id)));

        setOrder(next);
        reorder.callback({ ids: next });
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={tasks.map(task => task.id)} strategy={verticalListSortingStrategy}>
                {tasks.map(task => (
                    <SortableRow key={task.id} task={task} renderRow={props.renderRow} />
                ))}
            </SortableContext>
        </DndContext>
    );
}

function SortableRow(props: { task: Task; renderRow: (task: Task, grip: ReactNode) => ReactNode }) {
    const sortable = useSortable({ id: props.task.id });

    const grip = (
        <IconButton
            data-no-swipe
            ref={sortable.setActivatorNodeRef}
            {...sortable.attributes}
            {...sortable.listeners}
            onClick={e => e.stopPropagation()}
            aria-label="Drag to reorder"
            sx={{ width: 40, height: 40, my: -1, mr: -0.5, flexShrink: 0, touchAction: "none", color: "action.active" }}
        >
            <DragIndicator fontSize="small" />
        </IconButton>
    );

    return (
        <div
            ref={sortable.setNodeRef}
            style={{
                transform: CSS.Transform.toString(sortable.transform),
                transition: sortable.transition,
                position: "relative",
                zIndex: sortable.isDragging ? 2 : undefined,
                boxShadow: sortable.isDragging ? "0 2px 8px rgba(0, 0, 0, 0.2)" : undefined,
            }}
        >
            {props.renderRow(props.task, grip)}
        </div>
    );
}
